const DEVICE_ID_KEY = "ionemngr_device_id";

// IndexedDB 설정 (localStorage와 삭제 조건이 달라 상호 복구 채널로 사용)
const IDB_NAME = "ionemngr";
const IDB_STORE = "kv";

/**
 * UUID v4 생성 (crypto.randomUUID 폴백)
 * crypto.randomUUID()는 Secure Context(HTTPS/localhost)에서만 사용 가능하므로
 * HTTP + LAN IP 환경을 위한 폴백 포함
 */
function generateUUID(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // 폴백: crypto.getRandomValues 사용 (HTTP에서도 동작)
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 1
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// ── IndexedDB 헬퍼 (실패해도 앱 동작에 영향 없도록 모두 graceful) ──
function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    try {
      if (typeof indexedDB === "undefined") {
        reject(new Error("IndexedDB unsupported"));
        return;
      }
      const req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(IDB_STORE)) {
          db.createObjectStore(IDB_STORE);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } catch (e) {
      reject(e);
    }
  });
}

async function idbGet(key: string): Promise<string | null> {
  try {
    const db = await idbOpen();
    return await new Promise<string | null>((resolve) => {
      const tx = db.transaction(IDB_STORE, "readonly");
      const req = tx.objectStore(IDB_STORE).get(key);
      req.onsuccess = () => resolve((req.result as string) ?? null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function idbSet(key: string, value: string): Promise<void> {
  try {
    const db = await idbOpen();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  } catch {
    /* ignore */
  }
}

/**
 * 기기 ID를 모든 클라이언트 저장소(localStorage + IndexedDB)에 기록.
 * (서버 HttpOnly 쿠키는 로그인 응답 시 백엔드가 별도로 심는다)
 */
export function storeDeviceId(deviceId: string): void {
  try {
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  } catch {
    /* ignore */
  }
  // IndexedDB는 비동기 — 실패해도 무시 (fire-and-forget)
  void idbSet(DEVICE_ID_KEY, deviceId);
}

/**
 * 로그인 응답으로 서버가 확정한 device_id를 클라이언트 저장소에 반영.
 * (localStorage/IndexedDB가 모두 비었어도 서버가 쿠키로 복구한 값을 다시 심어 동기화)
 */
export function setDeviceIdFromServer(deviceId: string | null | undefined): void {
  if (deviceId) {
    storeDeviceId(deviceId);
  }
}

/**
 * 기기 ID를 비동기로 확정 (localStorage → IndexedDB 순으로 복구, 없으면 생성).
 * 어느 한쪽 저장소만 지워져도 다른 쪽에서 복구되어 동일 ID를 유지한다.
 * 앱 시작 시 1회 호출하여 localStorage를 미리 복구해 두는 용도.
 */
export async function ensureDeviceId(): Promise<string> {
  const ls = localStorage.getItem(DEVICE_ID_KEY);
  const idb = await idbGet(DEVICE_ID_KEY);
  const chosen = ls || idb || generateUUID();
  // 양쪽 저장소를 모두 chosen 값으로 동기화 (복구 + 신규 저장)
  storeDeviceId(chosen);
  return chosen;
}

/**
 * localStorage에서 기기 ID를 가져오거나, 없으면 새로 생성하여 저장 (동기).
 * 호출 즉시 값이 필요한 경우용. IndexedDB에도 미러링한다.
 */
export function getOrCreateDeviceId(): string {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = generateUUID();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  // IndexedDB에도 반영 (localStorage가 지워져도 복구되도록)
  void idbSet(DEVICE_ID_KEY, deviceId);
  return deviceId;
}

/**
 * 현재 저장된 기기 ID 반환 (없으면 null)
 */
export function getDeviceId(): string | null {
  return localStorage.getItem(DEVICE_ID_KEY);
}

/**
 * 브라우저/OS 정보를 간단히 파싱
 */
export function getDeviceDisplayName(): string {
  const ua = navigator.userAgent;
  let browser = "";
  let os = "";

  if (ua.includes("Edg/")) browser = "Edge";
  else if (ua.includes("Chrome") && !ua.includes("Edg")) browser = "Chrome";
  else if (ua.includes("Firefox")) browser = "Firefox";
  else if (ua.includes("Safari") && !ua.includes("Chrome")) browser = "Safari";

  if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Mac")) os = "macOS";
  else if (ua.includes("Linux") && !ua.includes("Android")) os = "Linux";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";

  if (browser && os) return `${browser} - ${os}`;
  if (browser) return browser;
  if (os) return os;
  return "내 디바이스";
}
